// @ts-nocheck
import { useState, useEffect, useRef, useCallback } from 'react'
import { useApi } from '../hooks/useApi'
import { QRCodeCanvas } from 'qrcode.react'
import { ExternalLink, Plus, Trash2, Copy, Check, ToggleLeft, ToggleRight, Phone, Pencil, QrCode, Download, X } from 'lucide-react'
import { formatPhoneNumberForDisplay } from '../utils/phoneNumberHelper'

interface PhoneNumber {
  phoneNumberId: string
  phoneNumber: string
  name: string
}

interface WebCallLink {
  linkId: string
  organizationId: string
  phoneNumberId: string
  displayName: string
  description?: string
  isActive: boolean
  createdAt: number
  updatedAt: number
}

interface Props {
  phoneNumbers: PhoneNumber[]
}

// 公開URLのベース（本番: https://telephone.dance、開発: window.location.origin）
const PUBLIC_BASE_URL = import.meta.env.VITE_PUBLIC_BASE_URL || window.location.origin

const getLinkUrl = (linkId: string) => `${PUBLIC_BASE_URL}/c/${linkId}`

/**
 * Web通話リンク管理コンポーネント
 * 公開URLを生成し、訪問者がブラウザから通話できるリンクを管理する
 */
export default function WebCallLinksSettings({ phoneNumbers }: Props) {
  const api = useApi()
  const [links, setLinks] = useState<WebCallLink[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [newLink, setNewLink] = useState({ phoneNumberId: '', displayName: '', description: '' })
  const [creating, setCreating] = useState(false)
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState({ displayName: '', description: '', phoneNumberId: '' })
  const [saving, setSaving] = useState(false)
  const [qrLinkId, setQrLinkId] = useState<string | null>(null)
  const qrCanvasRef = useRef<HTMLDivElement>(null)

  // リンク一覧を取得
  useEffect(() => {
    const fetchLinks = async () => {
      try {
        setLoading(true)
        const data = await api.get('/api/web-call-links')
        setLinks(data.items || [])
      } catch (err) {
        console.error('Web通話リンク取得エラー:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchLinks()
  }, [api])

  // リンク作成
  const handleCreate = async () => {
    if (!newLink.phoneNumberId || !newLink.displayName.trim()) return

    try {
      setCreating(true)
      const created = await api.post('/api/web-call-links', {
        phoneNumberId: newLink.phoneNumberId,
        displayName: newLink.displayName.trim(),
        ...(newLink.description.trim() && { description: newLink.description.trim() }),
      })
      setLinks([...links, created])
      setNewLink({ phoneNumberId: '', displayName: '', description: '' })
      setShowCreateForm(false)
    } catch (err) {
      alert(`作成エラー: ${err.message}`)
    } finally {
      setCreating(false)
    }
  }

  // 有効/無効切り替え
  const handleToggle = async (link: WebCallLink) => {
    try {
      const updated = await api.put(`/api/web-call-links/${link.linkId}`, {
        isActive: !link.isActive,
      })
      setLinks(links.map(l => l.linkId === link.linkId ? updated : l))
    } catch (err) {
      alert(`更新エラー: ${err.message}`)
    }
  }

  // 編集開始
  const handleEditStart = (link: WebCallLink) => {
    setEditingId(link.linkId)
    setEditForm({
      displayName: link.displayName,
      description: link.description || '',
      phoneNumberId: link.phoneNumberId,
    })
  }

  // 編集保存
  const handleEditSave = async () => {
    if (!editingId || !editForm.displayName.trim()) return

    try {
      setSaving(true)
      const updated = await api.put(`/api/web-call-links/${editingId}`, {
        displayName: editForm.displayName.trim(),
        description: editForm.description.trim(),
        phoneNumberId: editForm.phoneNumberId,
      })
      setLinks(links.map(l => l.linkId === editingId ? updated : l))
      setEditingId(null)
    } catch (err) {
      alert(`更新エラー: ${err.message}`)
    } finally {
      setSaving(false)
    }
  }

  // 削除
  const handleDelete = async (linkId: string) => {
    if (!confirm('このWeb通話リンクを削除しますか？')) return

    try {
      await api.delete(`/api/web-call-links/${linkId}`)
      setLinks(links.filter(l => l.linkId !== linkId))
    } catch (err) {
      alert(`削除エラー: ${err.message}`)
    }
  }

  // URLをコピー
  const handleCopy = (linkId: string) => {
    navigator.clipboard.writeText(getLinkUrl(linkId)).then(() => {
      setCopiedId(linkId)
      setTimeout(() => setCopiedId(null), 2000)
    })
  }

  // QRコードをダウンロード
  const handleDownloadQr = useCallback(() => {
    if (!qrCanvasRef.current || !qrLinkId) return
    const canvas = qrCanvasRef.current.querySelector('canvas')
    if (!canvas) return

    const link = links.find(l => l.linkId === qrLinkId)
    const fileName = link ? `qr-${link.displayName}.png` : `qr-${qrLinkId}.png`

    const url = canvas.toDataURL('image/png')
    const a = document.createElement('a')
    a.href = url
    a.download = fileName
    a.click()
  }, [qrLinkId, links])

  // 電話番号名を取得
  const getPhoneNumberName = (phoneNumberId: string): string => {
    const phone = phoneNumbers.find(p => p.phoneNumberId === phoneNumberId)
    return phone ? `${phone.name} (${formatPhoneNumberForDisplay(phone.phoneNumber)})` : '不明'
  }

  const qrLink = qrLinkId ? links.find(l => l.linkId === qrLinkId) : null

  return (
    <div className="space-y-6">
      {/* QRコードモーダル */}
      {qrLinkId && qrLink && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setQrLinkId(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-xs w-full p-6 text-center" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">QRコード</h3>
              <button onClick={() => setQrLinkId(null)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            <div ref={qrCanvasRef} className="flex justify-center mb-3">
              <QRCodeCanvas value={getLinkUrl(qrLinkId)} size={200} level="M" includeMargin />
            </div>
            <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">{qrLink.displayName}</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 font-mono mb-4 break-all">{getLinkUrl(qrLinkId)}</p>
            <button
              onClick={handleDownloadQr}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors text-sm font-medium flex items-center gap-2 mx-auto"
            >
              <Download className="w-4 h-4" />
              PNGをダウンロード
            </button>
          </div>
        </div>
      )}

      {/* ヘッダー */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center">
                <ExternalLink className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Web通話リンク</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">URLを共有して、ブラウザから直接通話を受けられます</p>
              </div>
            </div>
            <button
              onClick={() => setShowCreateForm(true)}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors flex items-center gap-2 text-sm font-medium"
              disabled={phoneNumbers.length === 0}
            >
              <Plus className="w-4 h-4" />
              新規作成
            </button>
          </div>
        </div>

        {/* 作成フォーム */}
        {showCreateForm && (
          <div className="p-6 bg-indigo-50 dark:bg-indigo-900/10 border-b border-gray-200 dark:border-gray-700">
            <div className="max-w-lg space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  電話番号
                </label>
                <select
                  value={newLink.phoneNumberId}
                  onChange={(e) => setNewLink({ ...newLink, phoneNumberId: e.target.value })}
                  className="input-field"
                >
                  <option value="">選択してください</option>
                  {phoneNumbers.map(phone => (
                    <option key={phone.phoneNumberId} value={phone.phoneNumberId}>
                      {phone.name} ({formatPhoneNumberForDisplay(phone.phoneNumber)})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  この電話番号の設定（キュー、営業時間等）がそのまま適用されます
                </p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  表示名
                </label>
                <input
                  type="text"
                  value={newLink.displayName}
                  onChange={(e) => setNewLink({ ...newLink, displayName: e.target.value })}
                  className="input-field"
                  placeholder="例: Webサイトからの問い合わせ"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  着信時にオペレータに表示される発信者名
                </p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  説明文 <span className="font-normal text-gray-400">(任意)</span>
                </label>
                <textarea
                  value={newLink.description}
                  onChange={(e) => setNewLink({ ...newLink, description: e.target.value })}
                  className="input-field"
                  placeholder="例: 営業時間 平日 9:00〜18:00"
                  rows={2}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  公開ページのタイトル下に表示されます
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleCreate}
                  disabled={creating || !newLink.phoneNumberId || !newLink.displayName.trim()}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors disabled:opacity-50 text-sm font-medium"
                >
                  {creating ? '作成中...' : '作成'}
                </button>
                <button
                  onClick={() => {
                    setShowCreateForm(false)
                    setNewLink({ phoneNumberId: '', displayName: '', description: '' })
                  }}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors text-sm"
                >
                  キャンセル
                </button>
              </div>
            </div>
          </div>
        )}

        {/* リンク一覧 */}
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {loading ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">読み込み中...</div>
          ) : links.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              Web通話リンクはまだありません
            </div>
          ) : (
            links.map(link => (
              <div key={link.linkId}>
                {editingId === link.linkId ? (
                  /* 編集フォーム */
                  <div className="p-4 bg-indigo-50 dark:bg-indigo-900/10 space-y-3">
                    <div className="max-w-lg space-y-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">電話番号</label>
                        <select
                          value={editForm.phoneNumberId}
                          onChange={(e) => setEditForm({ ...editForm, phoneNumberId: e.target.value })}
                          className="input-field text-sm"
                        >
                          {phoneNumbers.map(phone => (
                            <option key={phone.phoneNumberId} value={phone.phoneNumberId}>
                              {phone.name} ({formatPhoneNumberForDisplay(phone.phoneNumber)})
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">表示名</label>
                        <input
                          type="text"
                          value={editForm.displayName}
                          onChange={(e) => setEditForm({ ...editForm, displayName: e.target.value })}
                          className="input-field text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">説明文</label>
                        <textarea
                          value={editForm.description}
                          onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                          className="input-field text-sm"
                          rows={2}
                          placeholder="例: 営業時間 平日 9:00〜18:00"
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={handleEditSave}
                          disabled={saving || !editForm.displayName.trim()}
                          className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors disabled:opacity-50 text-sm font-medium"
                        >
                          {saving ? '保存中...' : '保存'}
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="px-3 py-1.5 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors text-sm"
                        >
                          キャンセル
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* 表示モード */
                  <div className="p-4 flex items-center gap-4">
                    {/* 状態アイコン */}
                    <button
                      onClick={() => handleToggle(link)}
                      className="flex-shrink-0"
                      title={link.isActive ? '有効 - クリックで無効化' : '無効 - クリックで有効化'}
                    >
                      {link.isActive ? (
                        <ToggleRight className="w-8 h-8 text-green-500" />
                      ) : (
                        <ToggleLeft className="w-8 h-8 text-gray-400" />
                      )}
                    </button>

                    {/* リンク情報 */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${link.isActive ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'}`}>
                          {link.displayName}
                        </span>
                        {!link.isActive && (
                          <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded">
                            無効
                          </span>
                        )}
                      </div>
                      {link.description && (
                        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 truncate">{link.description}</p>
                      )}
                      <div className="flex items-center gap-2 mt-1 text-sm text-gray-500 dark:text-gray-400">
                        <Phone className="w-3 h-3" />
                        <span>{getPhoneNumberName(link.phoneNumberId)}</span>
                      </div>
                      <div className="mt-1 text-xs text-gray-400 dark:text-gray-500 font-mono truncate">
                        {getLinkUrl(link.linkId)}
                      </div>
                    </div>

                    {/* アクション */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => handleEditStart(link)}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        title="編集"
                      >
                        <Pencil className="w-4 h-4 text-gray-500" />
                      </button>
                      <button
                        onClick={() => setQrLinkId(link.linkId)}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        title="QRコード"
                      >
                        <QrCode className="w-4 h-4 text-gray-500" />
                      </button>
                      <button
                        onClick={() => handleCopy(link.linkId)}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        title="URLをコピー"
                      >
                        {copiedId === link.linkId ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <Copy className="w-4 h-4 text-gray-500" />
                        )}
                      </button>
                      <a
                        href={`/c/${link.linkId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        title="プレビュー"
                      >
                        <ExternalLink className="w-4 h-4 text-gray-500" />
                      </a>
                      <button
                        onClick={() => handleDelete(link.linkId)}
                        className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        title="削除"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}
